/**
 * MSH HPB Landing Page — Client-side behaviour
 *
 * Responsibilities:
 *  1. Scroll-triggered entrance animations via IntersectionObserver
 *  2. Desktop sticky CTA visibility — shown once the hero scrolls out of view
 *  3. Mobile sticky CTA visibility — shown once the hero scrolls out of view
 *
 * All DOM queries are scoped to elements that exist within this page template.
 * The module is wrapped in an IIFE to avoid polluting the global scope.
 */
(function () {
  'use strict';

  /* ── 1. Entrance animations ─────────────────────────────────────────────── */

  /**
   * Targets both single-reveal and staggered-reveal containers.
   * Classes are applied so the CSS transition handles the actual animation.
   * Skipped entirely when an accessibility / keyboard-navigation tool is active
   * (detected via the `acsb-keynav` class on <body>).
   */
  if (!document.body.classList.contains('acsb-keynav')) {
    var staggerTargets = [
      'hpb-stat-grid',
      'hpb-bundle-grid',
      'hpb-reviews-grid',
      'hpb-food-list',
      'hpb-cmp-cards',
    ];

    var revealIO = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('hpb-in');
            revealIO.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    document
      .querySelectorAll(
        '.hpb-section, .hpb-stat-grid, .hpb-bundle-grid, .hpb-reviews-grid, .hpb-food-list, .hpb-cmp-table, .hpb-cmp-cards'
      )
      .forEach(function (el) {
        var isStagger = staggerTargets.some(function (cls) {
          return el.classList.contains(cls);
        });

        el.classList.add(isStagger ? 'hpb-reveal-stagger' : 'hpb-reveal');
        revealIO.observe(el);
      });
  }

  /* ── 2. Sticky CTA visibility ───────────────────────────────────────────── */

  var hero = document.querySelector('.hpb-hero');
  var deskSticky = document.getElementById('hpb-desk-sticky');
  var mobileSticky = document.getElementById('hpb-m-sticky');

  if (hero) {
    var stickyIO = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var heroVisible = entry.isIntersecting;

          if (deskSticky) {
            deskSticky.classList.toggle('hpb-show', !heroVisible);
          }

          if (mobileSticky) {
            mobileSticky.classList.toggle('hpb-show', !heroVisible);
          }
        });
      },
      { threshold: 0 }
    );

    stickyIO.observe(hero);
  }

  /* ── 3. Chat button — lift above sticky CTA when visible ───────────── */

  if (deskSticky || mobileSticky) {
    function liftChatButton(chatBtn) {
      var mobileBreakpoint = window.matchMedia('(max-width: 860px)');
  
      function updateChatBtnOffset() {
        var isMobile = mobileBreakpoint.matches;
        var activeSticky = isMobile
          ? (mobileSticky || deskSticky)
          : (deskSticky || mobileSticky);

        var isActive = activeSticky && (
          isMobile
            ? window.getComputedStyle(activeSticky).display !== 'none'
            : activeSticky.classList.contains('hpb-show')
        );

        if (isActive) {
          var rect = activeSticky.getBoundingClientRect();
          var stickyHeightFromBottom = window.innerHeight - rect.top;
          chatBtn.style.bottom = Math.round(stickyHeightFromBottom + 8) + 'px';
        } else {
          chatBtn.style.bottom = '';
        }
      }
  
      var stickyClassObserver = new MutationObserver(updateChatBtnOffset);
      var observeOpts = { attributes: true, attributeFilter: ['class'] };
  
      if (deskSticky) stickyClassObserver.observe(deskSticky, observeOpts);
      if (mobileSticky) stickyClassObserver.observe(mobileSticky, observeOpts);
  
      mobileBreakpoint.addEventListener('change', updateChatBtnOffset);
      window.addEventListener('resize', updateChatBtnOffset, { passive: true });
  
      updateChatBtnOffset();
    }
  
    var chatBtn = document.getElementById('chat-button');
  
    if (chatBtn) {
      liftChatButton(chatBtn);
    } else {
      // Chat widget may load asynchronously — watch for it
      var chatBtnObserver = new MutationObserver(function () {
        var btn = document.getElementById('chat-button');
        if (!btn) return;
  
        chatBtnObserver.disconnect();
        liftChatButton(btn);
      });
  
      chatBtnObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }
})();
